'use client';
import { useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { brainModelLabel, brainModelQualifiedLabel } from '../../lib/modelProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../components/ui/shadcn/dropdown-menu';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';
import { Button, buttonClassName } from '../../components/ui/Button';
import { cn } from '../../lib/utils';
import { ModelIcon } from '../../components/ui/ModelIcon';
import { ModelOptionList } from './ModelOptionList';

/** The trigger's skin. All three open the SAME menu over the same catalog; only the box differs.
 *
 *  `full` is the labelled header control and `compact` the dock's smaller twin. `statusline` is the one
 *  that sits in the metrics band under the conversation, where the model name used to be a plain read-out:
 *  it inherits the band's type and muted colour and stays unbordered at rest, because a boxed control
 *  in a row of quiet figures reads as the loudest thing on the page. It admits it is a control on hover,
 *  on focus and while its menu is open. */
type ModelPickerVariant = 'full' | 'compact' | 'statusline';

/** The ONE paint and layout line every variant wears: a trigger is an inline flex row of mark, name and
 *  chevron, centred on its line, in the muted ink, with the hover and the open-state colour of a control.
 *  `full` and `compact` then add their step through the shared class factory, and `statusline` adds only
 *  its own box — so the band's pill keeps the row and the hover it always had without stating a height.
 *
 *  It is handed to `buttonClassName`, which composes through `cn`, so its `hover:text-foreground` actually
 *  replaces the outlined variant's `hover:text-accent-foreground` instead of leaving the winner to
 *  stylesheet order. */
const TRIGGER_LAYOUT = 'flex items-center gap-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

const TRIGGER_CLASS: Record<ModelPickerVariant, string> = {
  full: buttonClassName('outline', 'default', `${TRIGGER_LAYOUT} max-w-55 justify-start px-2.5 text-conversation`),
  compact: buttonClassName('outline', 'default', `${TRIGGER_LAYOUT} max-w-32.5 justify-start px-2 text-small`),
  // No height and no type of its own: the statusline sets both, so the pill matches whichever host row it
  // lands in (the page toolbar's 11px band or the dock's smaller one) and cannot drift from its siblings.
  // The negative margin cancels the padding, so the model name starts exactly where the plain span did.
  // `w-fit` rather than a filled block: the studio skin stretches the model slot across the free row, and
  // a control that took all of it would paint a hover slab across half the band. `max-w-full` is what keeps
  // the truncation working when the slot is the narrower one instead.
  statusline: cn(TRIGGER_LAYOUT, 'h-auto -mx-1 w-fit min-w-0 max-w-full rounded px-1 data-[state=open]:bg-accent data-[state=open]:text-foreground'),
};

/** The shared model picker: a trigger button (current model + chevron) opening a grouped popover of every
 *  selectable model. Reads the single catalog + switch action off the chat controller (no props catalog,
 *  no second fetch). `full` is a labelled header control, `compact` an icon-sized dock button, and
 *  `statusline` the pill in the metrics band — same component, same data, same menu. Selecting a model
 *  switches the conversation IN PLACE (no SSE reconnect).
 *
 *  Its open state is the chat controller's `modelOpen`, so `/model` opens whichever picker is mounted (the
 *  surface mounts exactly one: the statusline's, or the bar's, which on a phone sits in the overflow menu).
 *  The rows themselves are ModelOptionList. */
export function ModelPicker({ variant = 'full' }: { variant?: ModelPickerVariant }) {
  const { t } = useTranslation();
  const { models, currentModel, provider, providerLabel, modelsLoading, loadModels, modelStatus, modelQueued, retryModel, modelOpen, setModelOpen } = useBrainChat();
  const firstOpenHandled = useRef(false);
  const { readOnly, childFocus, guest } = useBrainChatStatus();

  const handleOpenChange = (next: boolean): void => {
    setModelOpen(next);
    if (!next || firstOpenHandled.current) return;
    firstOpenHandled.current = true;
    if (models === null && !modelsLoading) loadModels();
  };

  const label = currentModel ? brainModelLabel({ model: currentModel }) : t.brainChat.modelPicker;
  const qualifiedLabel = currentModel
    ? brainModelQualifiedLabel({ provider, providerLabel, model: currentModel })
    : t.brainChat.modelPicker;

  const inStatusline = variant === 'statusline';
  if (readOnly && !childFocus) return null;

  return (
    <div
      data-testid="chat-model-picker"
      // In the statusline the pill takes the slot the plain model span held, `data-stat` included: the
      // studio skin's width rule addresses the model by that attribute, and tests tell it from the
      // statistics that drop when the row is full.
      {...(inStatusline ? { 'data-stat': 'model' } : {})}
      // One row: a status beside the trigger, never wrapped under it where the composer would cover it.
      className={inStatusline ? 'relative flex min-w-0 items-center gap-2' : 'relative flex shrink-0 items-center gap-2'}
    >
      <DropdownMenu open={modelOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={variant === 'statusline' ? 'ghost' : 'outline'}
            // A guest sees the owner's model and why it is not theirs to change.
            // An unknown outcome holds every pick until the daemon's model has been read back.
            disabled={guest || modelStatus === 'unconfirmed'}
            aria-busy={modelStatus === 'saving' || undefined}
            title={guest ? t.chat.ownerOnlyControl : qualifiedLabel}
            // Deliberately not aria-labelled: the visible catalog model name is the trigger's accessible
            // name. The title keeps the provider-qualified identity available for diagnostics, while Radix
            // supplies aria-haspopup/aria-expanded.
            className={TRIGGER_CLASS[variant]}
          >
            {/* The brand mark the menu rows already carry, so the current model is recognisable at a
                glance and the pill and the list it opens speak the same vocabulary. It is monochrome-safe
                by construction: ModelIcon masks a mono asset with currentColor. */}
            {inStatusline && currentModel ? <ModelIcon name={currentModel} size={12} /> : null}
            <span className="truncate">{label}</span>
            <ChevronDown size={variant === 'full' ? 14 : 12} className="shrink-0 opacity-60" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          aria-label={t.brainChat.modelPicker}
          // Opens from the pill's left edge toward the conversation, not back over the sidebar.
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="max-h-80 w-[min(--spacing(128),calc(100vw_-_--spacing(4)))]"
        >
          <ModelOptionList onPickAction={() => setModelOpen(false)} />
        </DropdownMenuContent>
      </DropdownMenu>
      {modelQueued
        ? <span role="status" aria-live="polite" title={t.brainChat.modelSwitchQueued} className="min-w-0 truncate text-meta text-warning">{t.brainChat.modelSwitchQueued}</span>
        // The failure itself is reported once, by the controller's toast; this is only the way to try again.
        : modelStatus === 'error' ? <Button type="button" variant="ghost" onClick={retryModel}>{t.common.retry}</Button> : null}
    </div>
  );
}
